/* eslint-disable no-console -- console sink for development logging */
import { APP_CONFIG } from '@/config/app.config';

import type { Logger, LogContext, LogLevel } from './types';

class ConsoleLogger implements Logger {
  private shouldLog(level: LogLevel): boolean {
    if (!APP_CONFIG.logging.enabled) {
      return false;
    }
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const configLevel = APP_CONFIG.logging.level as LogLevel;
    const configLevelIndex = levels.indexOf(configLevel);
    const currentLevelIndex = levels.indexOf(level);
    return currentLevelIndex >= configLevelIndex;
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` | ${JSON.stringify(context)}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog('debug')) {
      console.log(this.formatMessage('debug', message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage('info', message, context));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message, context));
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    if (this.shouldLog('error')) {
      const errorContext = error
        ? { ...context, error: error.message, stack: error.stack }
        : context;
      console.error(this.formatMessage('error', message, errorContext));
    }
  }
}

export const consoleLogger = new ConsoleLogger();
